import { DAYS } from 'constants/days';
import { ExecutionStatus } from 'sync/maintenance-job-service';
import { MaintenanceScheduleResponse } from 'sync/maintenance-schedule-service';
import { NotificationLevel, NotificationMessage } from 'types/notifications-slice';
import { displayTime, fromNow } from 'utils/datetime';
import dayjs from 'utils/dayjs';
import { Day, Time } from './maintenance.types';

/*
 * Format the return of the maintenance payload.
 * We then pad them incase it's a single digit.
 */
export function formatMaintenanceTime(day: Day, time: Time) {
  const hourText = time.hour?.toString().padStart(2, '0');
  const minuteText = time.minute?.toString().padStart(2, '0');

  return `${DAYS[day].label}, ${hourText}:${minuteText} (${dayjs().format('z')})`;
}

/**
 * Returns a formatted timestamp based on the status of the
 * maintenance job.
 *
 * @remarks
 * Passing true to the from function removes the label suffix.
 * For example, 'a year ago' displays as 'a year'.
 *
 * @see
 * https://day.js.org/docs/en/display/from
 */
export function getFormattedTimestamp(status: ExecutionStatus, time: string, modifiedAt: string) {
  const dTime = displayTime(String(new Date(time)));

  switch (status) {
    case ExecutionStatus.FailedToQueue:
    case ExecutionStatus.Running:
      return `${fromNow(time)}, ${dTime}`;
    case ExecutionStatus.Completed:
      return `${dayjs(modifiedAt).from(time, true)}`;
    default:
      return dTime;
  }
}

/**
 * Returns a formatted timestamp label based on the
 * status of the maintenace job.
 */
export function getTimestampLabel(status: ExecutionStatus | undefined) {
  switch (status) {
    case ExecutionStatus.FailedToQueue:
    case ExecutionStatus.Running:
      return 'Start Time';
    case ExecutionStatus.Completed:
      return 'Duration';
    default:
      return 'Scheduled';
  }
}

/**
 * Converts local time input to UTC format.
 */
export function transformLocaltoUTC(day: Day, time: Time) {
  return dayjs().weekday(day).set('hour', time.hour!).set('minute', time.minute!).utc();
}

/**
 * Converts UTC time input to local format.
 */
export function transformUTCToLocal(day: Day, time: Time) {
  return dayjs().utc().weekday(day).set('hour', time.hour!).set('minute', time.minute!).local();
}

/**
 * Validates the time of the preferred maintenance
 * schedule.
 * Ensures the values are between 0 and 24, and in
 * the format of HH:MM.
 */
export function validateTime(time: string) {
  const hourMinute = time.split(':');
  const hour = Number(hourMinute[0]);
  const minute = Number(hourMinute[1]);
  return hour >= 0 && hour <= 23 && minute >= 0 && minute <= 59 ? '' : 'should have the following format HH:MM.';
}

export const formatMaintenanceScheduleTime = (maintainenceSchedule: MaintenanceScheduleResponse | undefined) => {
  let day: Day = dayjs().get('d') as Day;
  let time: Time = { hour: dayjs().get('hour'), minute: dayjs().get('minute') };
  let saveDay: Day = dayjs().get('d') as Day;
  let saveTime: Time = { hour: dayjs().get('hour'), minute: dayjs().get('minute') };
  let setPreference = false;
  let preferredTime = 'Not set yet.';
  if (maintainenceSchedule) {
    const { data } = maintainenceSchedule;

    if (data) {
      setPreference = true;
      const utcDay = data.day;
      const utcTime = { hour: data.hour, minute: data.minute };
      const localDayTime = transformUTCToLocal(utcDay, utcTime);
      day = localDayTime.day() as Day;
      time = { hour: localDayTime.get('hour'), minute: localDayTime.get('minute') };
      saveDay = day;
      saveTime = time;
      preferredTime = formatMaintenanceTime(day, time);
    }
  }

  return {
    day,
    time,
    saveDay,
    saveTime,
    setPreference,
    preferredTime,
  };
};

export const checkIsTimeToPendingMaintenanceReached = (isHealthy?: boolean, isUpgrading?: boolean, timeToPendingMaintenance?: number) => {
  if (isUpgrading) {
    return true;
  }

  if (timeToPendingMaintenance === undefined || !isHealthy) {
    return false;
  }

  return timeToPendingMaintenance <= 5000000000; // 5 seconds for sanity;
};

export const checkPendingMaintenanceQueueAtReached = (queueAt?: string, status?: ExecutionStatus) => {
  if (status === ExecutionStatus.Pending && queueAt) {
    return dayjs().add(5, 'seconds').isAfter(dayjs(queueAt));
  }
  return false;
};

type CheckAndMaybeNotifyAboutCancelledMaintenanceArgs = {
  entityName: 'cluster' | 'app service';
  previousCancelledInfo: () => Record<
    string,
    {
      lastCancelledAt: string;
      lastCancelledBy: string;
      hasBeenDismissed: boolean;
    }
  >;
  entitiesToCheck: {
    name: string;
    id: string;
    maintenance: {
      lastCancelledAt?: string;
      lastCancelledBy?: string;
    };
  }[];
  userId?: string;
  createNotification: (notification: {
    level: NotificationLevel;
    message: NotificationMessage;
    onDismiss?: () => void;
    urlScope?: string;
    shouldClearNext?: boolean;
  }) => void;
  saveLastCancelledInfo: (
    previousCancelledInfo: Record<string, { lastCancelledAt: string; lastCancelledBy: string; hasBeenDismissed: boolean }>
  ) => void;
};

/**
 * Has support or another user cancelled a maintenance for any cluster(s)? If so, notify the user about it (one notification, once).
 */
export function checkAndMaybeNotifyAboutCancelledMaintenance(args: CheckAndMaybeNotifyAboutCancelledMaintenanceArgs) {
  const { entityName, userId, entitiesToCheck, createNotification, saveLastCancelledInfo } = args;

  const previousCancelledInfo = args.previousCancelledInfo();

  const isNewTimestamp = (entity: {
    name: string;
    id: string;
    maintenance: {
      lastCancelledAt?: string;
      lastCancelledBy?: string;
    };
  }) => {
    if (!entity?.maintenance?.lastCancelledAt || !entity?.maintenance?.lastCancelledBy || !userId) {
      return false;
    }

    const currentTimestamp = entity?.maintenance.lastCancelledAt;

    const previousTimestamp = previousCancelledInfo[entity.id]?.lastCancelledAt;

    return !previousTimestamp || previousTimestamp !== currentTimestamp;
  };

  // 1/3 Check if any cluster maintenance has been cancelled by support or another user.
  const entitiesWithMaintenanceCancelled = entitiesToCheck.filter((entity) => {
    if (!entity?.maintenance?.lastCancelledAt || !entity?.maintenance?.lastCancelledBy || !userId) {
      return false;
    }

    const isCancelledByAnotherUser = entity?.maintenance.lastCancelledBy !== userId;
    const hasBeenDismissed = previousCancelledInfo[entity.id]?.hasBeenDismissed;

    return isCancelledByAnotherUser && (isNewTimestamp(entity) || !hasBeenDismissed);
  });

  if (entitiesWithMaintenanceCancelled.length > 0) {
    // 2/3 Notify about the cancelled maintenance.
    const entityNames = entitiesWithMaintenanceCancelled.map((entity) => entity.name).join(', ');
    createNotification({
      level: 'info',
      message: `Maintenance has been cancelled for the following ${entityName}(s): ${entityNames}`,
      shouldClearNext: true,
      onDismiss: () => {
        entitiesWithMaintenanceCancelled.forEach((entity) => {
          if (entity?.maintenance.lastCancelledAt && entity?.maintenance.lastCancelledBy) {
            previousCancelledInfo[entity.id] = {
              lastCancelledAt: entity?.maintenance.lastCancelledAt,
              lastCancelledBy: entity?.maintenance.lastCancelledBy,
              hasBeenDismissed: true,
            };
          }
        });
        saveLastCancelledInfo(previousCancelledInfo);
      },
    });

    // 3/3 Local store the last cancelled info for each cluster.
    entitiesWithMaintenanceCancelled.forEach((entity) => {
      if (entity?.maintenance.lastCancelledAt && entity?.maintenance.lastCancelledBy) {
        previousCancelledInfo[entity.id] = {
          hasBeenDismissed: isNewTimestamp(entity) ? false : previousCancelledInfo[entity.id]?.hasBeenDismissed,
          lastCancelledAt: entity?.maintenance.lastCancelledAt,
          lastCancelledBy: entity?.maintenance.lastCancelledBy,
        };
      }
    });
    saveLastCancelledInfo(previousCancelledInfo);
  }
}
